import React, { useEffect } from 'react'
import { Button, Box, Menu, MenuItem, Typography } from '@mui/material'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import { BacktestType } from '../../../../app/showcase/contexts/ShowcaseContext'

// Extend Window interface for tour functionality
declare global {
  interface Window {
    closeTourTypeSelectMenu?: () => void
  }
}

interface TypeSelectProps {
  type: BacktestType
  setType: (type: BacktestType) => void
}

const typeOptions: { value: BacktestType; label: string }[] = [
  { value: 'showcase', label: 'Showcase' },
  { value: 'user', label: 'Your Backtests' },
  { value: 'favorites', label: 'Favorites' },
  { value: 'featured', label: 'Basic' },
]

function TypeSelect({ type, setType }: TypeSelectProps) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  useEffect(() => {
    // Attach the function to the window object
    window.closeTourTypeSelectMenu = handleClose

    // Return a cleanup function to remove it when the component unmounts
    return () => {
      delete window.closeTourTypeSelectMenu
    }
  }, [])

  const handleSelect = (value: BacktestType) => {
    setType(value)
    handleClose()
  }

  const getCurrentLabel = () => {
    return typeOptions.find((option) => option.value === type)?.label || 'Select Type'
  }

  return (
    <Box>
      <Button
        variant="outlined"
        onClick={handleClick}
        sx={{
          '&:hover': {
            borderColor: 'white',
            backgroundColor: 'transparent',
          },
          color: 'rgba(255, 255, 255, 0.7)',
          borderColor: 'rgba(255, 255, 255, 0.23)',
          height: 40,
          minWidth: 160,
          justifyContent: 'space-between',
          textTransform: 'none',
        }}
        data-tour-id="type-select-button"
        endIcon={open ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
      >
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
          {getCurrentLabel()}
        </Typography>
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        MenuListProps={
          {
            'data-tour-id': 'type-select-menu',
          } as any
        }
      >
        {typeOptions.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => handleSelect(option.value)}
            selected={type === option.value}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  )
}

export default TypeSelect
